import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import type { Notification } from '../../utils/notificationService';

interface NotificationDropdownProps {
  className?: string;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ className = '' }) => {
  const { state, markAsRead, markAllAsRead, fetchNotifications } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  // Load unread notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      console.log('Dropdown opened, fetching notifications...');
      fetchNotifications({ per_page: 5, status: 'unread' });
    }
  }, [isOpen, fetchNotifications]);

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Dropdown clicked, toggling...');
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.notifications.dropdown')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead([notification.id]);
    }
    // Handle navigation based on notification type or related object
    // This could navigate to specific pages based on the notification
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // If unauthorized, don't render the bell at all
  if (state.unauthorized) return null;

  return (
    <div className={`notifications dropdown ${className}`}>
      <a
        href="#"
        role="button"
        className="position-relative"
        id="notificationDropdown"
        style={{ cursor: 'pointer' }}
        onClick={handleDropdownToggle}
      >
        {state.unreadCount > 0 && (
          <span className="count text-bg-danger" id="notificationCount">
            {state.unreadCount > 99 ? '99+' : state.unreadCount}
          </span>
        )}
        <span className="fa fa-bell-o"></span>
      </a>

      {isOpen && (
        <ul
          className="dropdown-notif dropdown-menu dropdown-menu-end border-primary p-0 mt-2 show"
          id="notificationList"
          style={{ 
            maxHeight: '400px', 
            overflowY: 'auto', 
            minWidth: '350px',
            position: 'absolute',
            top: '100%',
            right: '0',
            zIndex: 1000,
            display: 'block'
          }}
        >
          {/* Header - matching Django structure exactly */}
          <li className="dropdown-header d-flex justify-content-between align-items-center p-3 border-bottom">
            <h6 className="mb-0">Notifications</h6>
            {state.unreadCount > 0 && (
              <button 
                className="btn btn-sm btn-outline-primary" 
                id="markAllRead"
                onClick={handleMarkAllAsRead}
              >
                Mark all read
              </button>
            )}
          </li>

          {/* Notifications List - matching Django structure exactly */}
          <div id="notificationItems">
            {state.loading ? (
              <li className="dropdown-item text-center py-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </li>
            ) : state.notifications.length === 0 ? (
              <li className="dropdown-item text-center py-3">
                <i className="bi bi-bell-slash text-muted" style={{ fontSize: '2rem' }}></i>
                <p className="text-muted mb-0 mt-2">No unread notifications</p>
              </li>
            ) : (
              state.notifications.map((notification) => (
                <li key={notification.id} className="border-bottom border-primary position-relative">
                  <div 
                    className={`dropdown-item ${!notification.is_read ? 'unread' : ''}`}
                    data-notification-id={notification.id}
                  >
                    <a 
                      href="#" 
                      className="d-flex justify-content-between align-items-center notification-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNotificationClick(notification);
                      }}
                    >
                      <div className="dropdown-box-text">
                        <p className="text-secondary text-notif mb-1 pe-2 d-flex flex-wrap">
                          <span>{notification.title}</span>
                        </p>
                        <p className="text-secondary text-notif mb-1 pe-2">
                          {notification.message.length > 60 
                            ? `${notification.message.substring(0, 60)}...` 
                            : notification.message
                          }
                        </p>
                        <p className="text-secondary text-notif text-end small mb-0">
                          {formatDate(notification.created_at)}
                        </p>
                      </div>
                      {!notification.is_read && (
                        <span className="badge rounded-circle bg-primary ms-2">&nbsp;</span>
                      )}
                    </a>
                  </div>
                </li>
              ))
            )}
          </div>

          {/* Footer - matching Django structure exactly */}
          <li className="border-top">
            <Link
              to="/notifications"
              className="btn btn-outline-primary w-100 fw-semibold btn-notif"
            >
              View All Notifications
            </Link>
          </li>
        </ul>
      )}
    </div>
  );
};

export default NotificationDropdown; 